// frontend/src/pages/EditCircleDetailsPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "api";
import BackButton from "../components/BackButton";
import { COUNTRY_OPTIONS } from "../utils/countries";

const codeToFlag = (countryCode = "") => {
  const code = String(countryCode).trim().toUpperCase();

  if (code.length !== 2) return "";

  return code
    .split("")
    .map((character) =>
      String.fromCodePoint(127397 + character.charCodeAt(0))
    )
    .join("");
};

const normalizeWebsite = (value = "") => {
  const trimmed = String(value).trim();

  if (!trimmed) return null;

  return /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
};

const isValidWebsite = (value = "") => {
  const trimmed = String(value).trim();

  // Website is optional.
  if (!trimmed) return true;

  try {
    const parsed = new URL(normalizeWebsite(trimmed));

    const hostname = parsed.hostname;

    return (
      ["http:", "https:"].includes(parsed.protocol) &&
      hostname.includes(".") &&
      !hostname.startsWith(".") &&
      !hostname.endsWith(".") &&
      !hostname.includes("..")
    );
  } catch {
    return false;
  }
};

const getCityFromCircle = (circle) => {
  const directCity =
    circle?.city ||
    circle?.location_city ||
    circle?.location?.city ||
    circle?.address?.city ||
    circle?.locality;

  if (directCity) {
    return String(directCity).trim();
  }

  // Fallback for APIs that only return a combined location string,
  // such as "New York, United States".
  const combinedLocation =
    circle?.location_text ||
    circle?.location_label ||
    circle?.location ||
    "";

  if (typeof combinedLocation === "string" && combinedLocation.trim()) {
    return combinedLocation.split(",")[0].trim();
  }

  return "";
};

const getCountryCodeFromCircle = (circle) => {
  return String(
    circle?.country_code ||
      circle?.countryCode ||
      circle?.location_country_code ||
      circle?.location?.country_code ||
      circle?.location?.countryCode ||
      circle?.address?.country_code ||
      ""
  )
    .trim()
    .toUpperCase();
};

export default function EditCircleDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [website, setWebsite] = useState("");
  const [description, setDescription] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const circleId = Number(id);

  const websiteIsValid = isValidWebsite(website);

  const locationLabel = useMemo(() => {
    const country = COUNTRY_OPTIONS.find(
      (option) => option.code === countryCode
    );

    return [city.trim(), country?.name].filter(Boolean).join(", ");
  }, [city, countryCode]);

  useEffect(() => {
    let cancelled = false;

    const loadCircle = async () => {
      setLoading(true);
      setErr("");

      try {
        const response = await api.get(`/groups/${circleId}`);

        if (cancelled) return;

        const circle =
          response.data?.group ||
          response.data?.circle ||
          response.data;

        setName(circle?.name || "");
        setCity(getCityFromCircle(circle));
        setCountryCode(getCountryCodeFromCircle(circle));
        setWebsite(
          circle?.website ||
            circle?.website_url ||
            circle?.url ||
            ""
        );
        setDescription(circle?.description || "");
      } catch (error) {
        if (cancelled) return;

        setErr(
          error?.response?.data?.detail ||
            "Failed to load circle details."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (Number.isFinite(circleId) && circleId > 0) {
      loadCircle();
    } else {
      setErr("Invalid circle.");
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [circleId]);

  const goBackToCircle = () => {
    navigate(`/circles/${id}`);
  };

  const canSave =
    Boolean(name.trim()) &&
    Boolean(city.trim()) &&
    Boolean(countryCode) &&
    websiteIsValid &&
    !saving;

  const submit = async (event) => {
    event.preventDefault();

    if (!canSave) return;

    setSaving(true);
    setErr("");

    try {
      const payload = {
        name: name.trim(),
        city: city.trim(),
        country_code: countryCode,
        website: normalizeWebsite(website),
        description: description.trim() || null,
      };

      await api.patch(`/groups/${circleId}`, payload);

      navigate(`/circles/${id}?detailsUpdated=1`, {
        replace: true,
      });
    } catch (error) {
      setErr(
        error?.response?.data?.detail ||
          "Failed to save circle details."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-blue-50">
      <header
        className="sticky top-0 z-30 bg-blue-50 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="relative mx-auto flex h-14 max-w-5xl items-center px-4">
          <BackButton
            className="text-blue-600"
            ariaLabel="Back"
          />

          <h1 className="absolute left-1/4 -translate-x-10 whitespace-nowrap text-[22px] font-black leading-none tracking-[-0.04em] text-blue-600">
            Edit Circle
          </h1>
        </div>
      </header>

      <main
        className="px-5 pb-6"
        style={{
          paddingBottom:
            "calc(env(safe-area-inset-bottom) + 24px)",
        }}
      >
        {loading ? (
          <p className="py-6 text-sm text-gray-600">
            Loading circle details…
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <div>
              <label className="mb-1 ml-3 block text-sm font-medium">
                Circle Name
              </label>

              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Circle name"
                className="w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              />
            </div>

            <div>
              <label className="mb-1 ml-3 block text-sm font-medium">
                City <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder="e.g. New York"
                className="w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              />
            </div>

            <div>
              <label className="mb-1 ml-3 block text-sm font-medium">
                Country <span className="text-red-500">*</span>
              </label>

              <select
                value={countryCode}
                onChange={(event) =>
                  setCountryCode(event.target.value)
                }
                className="w-full rounded-2xl border bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                required
              >
                <option value="" disabled>
                  Select country
                </option>

                {COUNTRY_OPTIONS.map((option) => (
                  <option
                    key={option.code}
                    value={option.code}
                  >
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            {(city || countryCode) && (
              <div className="ml-3 text-sm text-gray-600">
                {countryCode && (
                  <span className="mr-1">
                    {codeToFlag(countryCode)}
                  </span>
                )}

                {locationLabel}
              </div>
            )}

            <div>
              <label className="mb-1 ml-3 block text-sm font-medium">
                Website
              </label>

              <input
                type="text"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={website}
                onChange={(event) =>
                  setWebsite(event.target.value)
                }
                placeholder="example.com"
                aria-invalid={!websiteIsValid}
                className={`w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                  websiteIsValid
                    ? "focus:ring-blue-600"
                    : "border-red-500 focus:ring-1 focus:ring-red-500"
                }`}
              />

              {!websiteIsValid && (
                <p className="ml-3 mt-1 text-xs text-red-600">
                  Enter a valid website
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 ml-3 block text-sm font-medium">
                Description
              </label>

              <textarea
                rows={4}
                value={description}
                onChange={(event) =>
                  setDescription(event.target.value)
                }
                placeholder="Tell us about your circle!"
                className="w-full rounded-2xl border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            {err && (
              <p className="text-sm text-red-600">
                {err}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={goBackToCircle}
                disabled={saving}
                className="!h-8 !min-h-0 rounded-full bg-white px-3 text-sm font-medium text-blue-600 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!canSave}
                className="!h-8 !min-h-0 rounded-full bg-blue-600 px-3 text-sm font-medium text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}